function loadScriptOnce(src, globalName) {
  if (window[globalName]) return Promise.resolve(window[globalName]);

  const existing = document.querySelector(`script[data-partbot-src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window[globalName]), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.partbotSrc = src;
    script.onload = () => resolve(window[globalName]);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function normalizeSearchHost(host, fallback) {
  const value = host || fallback;
  return value.startsWith("http") ? value : `https://${value}`;
}

function sortedRequestOnPrimaryIndex(request, primaryIndexName, { dropSort = false } = {}) {
  if (!request || !primaryIndexName) return request;

  const requestIndexName = request.indexName || request.indexUid || "";
  if (requestIndexName === primaryIndexName && dropSort && request.params?.sort) {
    const { sort, ...params } = request.params;
    return {
      ...request,
      params,
    };
  }

  const sortPrefix = `${primaryIndexName}:`;
  if (!requestIndexName.startsWith(sortPrefix)) return request;

  const sortValue = requestIndexName.slice(sortPrefix.length);
  return {
    ...request,
    indexName: primaryIndexName,
    indexUid: request.indexUid ? primaryIndexName : request.indexUid,
    params: {
      ...(request.params || {}),
      ...(dropSort || !sortValue ? {} : { sort: [sortValue] }),
    },
  };
}

function sortedRequestsOnPrimaryIndex(requests, primaryIndexName, options = {}) {
  if (!Array.isArray(requests)) return requests;
  return requests.map((request) => sortedRequestOnPrimaryIndex(request, primaryIndexName, options));
}

function isUnsupportedSortError(error) {
  const message = String(error?.message || error || "");
  return message.includes("is not sortable") || message.includes("invalid_search_sort");
}


function resolveInitialSortIndex(searchIndexName, preferredSort) {
  const sortParam = resolveSortParam(preferredSort);
  return sortParam ? `${searchIndexName}:${sortParam}` : searchIndexName;
}

function resolveSortParam(preferredSort) {
  const sort = String(preferredSort || "").trim().toLowerCase();
  if (!sort || sort === "manual" || sort === "relevance") return "";

  if (sort.includes(":")) {
    const parts = sort.split(":");
    return parts.slice(-2).join(":");
  }

  if (sort === "best-selling" || sort === "best_selling") return "quantity_sold:desc";
  if (sort === "price-ascending" || sort === "price_ascending") return "price_from:asc";
  if (sort === "price-descending" || sort === "price_descending") return "price_from:desc";
  if (sort === "title-ascending" || sort === "title_ascending") return "title:asc";
  if (sort === "title-descending" || sort === "title_descending") return "title:desc";

  return "";
}


async function fetchSearchConfig(apiHost, apiKey, store) {
  if (store) {
    try {
      const response = await fetch(
        `${apiHost}/v1/storefront-search-configs/${encodeURIComponent(store)}.js?v=${Math.floor(Date.now() / 60_000)}`,
        { credentials: "omit" },
      );
      if (!response.ok) throw new Error(`Search config failed: ${response.status}`);

      const config = await response.json();
      if (config.host && config.store_search_key && config.store_index_name) {
        return config;
      }
    } catch (error) {
      console.warn("[PartbotCollectionSearch] Public search config unavailable; using auth", error);
    }
  }

  const response = await fetch(`${apiHost}/v1/vehicles/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      use_store_vehicles: true,
      ...(store ? { store } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(`Vehicle auth failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

function normalizeFacetAttribute(facet) {
  if (!facet) return facet;
  if (facet.includes("Brand") || facet.includes("Vendor") || facet === "brand" || facet === "vendor") {
    return "brand";
  }
  if (facet.includes("Product Type") || facet === "product_type") {
    return "product_type";
  }
  if (facet.includes("Kit") || facet.includes("Assembly") || facet === "assembly") {
    return "assembly";
  }
  return facet.includes(".") ? facet : `attributes.${facet}`;
}

function uniq(values) {
  return [...new Set(values.filter(Boolean))];
}

function filterValue(value, { numeric = false } = {}) {
  const stringValue = String(value).trim();
  if (numeric && /^-?\d+(\.\d+)?$/.test(stringValue)) return stringValue;
  return JSON.stringify(stringValue);
}

function quoteFilterAttribute(attribute) {
  const unquotedAttribute = String(attribute || "")
    .trim()
    .replace(/^["']|["']$/g, "");

  if (!unquotedAttribute) return unquotedAttribute;
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(unquotedAttribute)) return unquotedAttribute;
  return JSON.stringify(unquotedAttribute);
}

function equalsFilter(attribute, value, options = {}) {
  return `${quoteFilterAttribute(attribute)} = ${filterValue(value, options)}`;
}

function priceFilterValue(value) {
  const normalizedValue = String(value || "").replace(/,/g, "").trim();
  if (!normalizedValue) return null;

  const price = Number(normalizedValue);
  if (!Number.isFinite(price) || price < 0) return null;

  return price;
}

function normalizeLegacyFilterValue(value) {
  const trimmedValue = String(value || "").trim();

  if (/^(true|false)$/i.test(trimmedValue)) return trimmedValue.toLowerCase();
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return trimmedValue;

  if (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
  ) {
    return filterValue(trimmedValue.slice(1, -1).replace(/\\(["'])/g, "$1"));
  }

  return filterValue(trimmedValue);
}

function normalizeLegacySearchFilters(filters) {
  if (!filters) return "";

  return filters.replace(
    /(^|[\s(])((?:"[^"]+"|'[^']+'|[A-Za-z_][A-Za-z0-9_.[\]-]*))\s*:\s*("(?:\\"|[^"])*"|'(?:\\'|[^'])*'|true|false|-?\d+(?:\.\d+)?|[^\s)]+)/gi,
    (match, prefix, attribute, value) =>
      `${prefix}${quoteFilterAttribute(attribute)} = ${normalizeLegacyFilterValue(value)}`,
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function maskKey(key) {
  if (!key) return key;
  if (key.length <= 12) return key;
  return `${key.slice(0, 6)}...${key.slice(-6)}`;
}

function normalizeFilterLabel(label) {
  return String(label || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const SHOPIFY_COLLECTION_PARAM_PATTERNS = [
  /^sort_by$/,
  /^filter\./,
  /^page$/,
  /^q$/,
  /^type$/,
  /^options\[/,
  /^section_id$/,
];

const VEHICLE_SEARCH_PARAM_NAMES = [
  "make",
  "model",
  "year",
  "body",
  "series",
  "engine",
  "engine_capacity",
];

const NATIVE_FILTER_LABELS_HIDDEN_WITH_VEHICLE = ["vehicle make", "vehicle model"];

// Ask Shopify for membership instead of reconstructing collection rules.
// Forward native filters and tagged paths so membership matches the rendered URL.
function collectionMembershipUrl(collectionHandle, page) {
  const root = window.Shopify?.routes?.root || "/";
  const basePath = `${root.endsWith("/") ? root : `${root}/`}collections/${encodeURIComponent(collectionHandle)}`;
  const current = new URL(window.location.href);
  const path = current.pathname === basePath || current.pathname.startsWith(`${basePath}/`)
    ? current.pathname : basePath;
  const url = new URL(path, window.location.origin);
  current.searchParams.forEach((value, name) => {
    if (name.startsWith("filter.") || name === "sort_by") url.searchParams.append(name, value);
  });
  url.searchParams.set("view", "partbot-product-ids");
  url.searchParams.set("page", String(page));
  return url;
}

function needsCollectionMembership(collectionHandle) {
  if (!collectionHandle) throw new Error("Missing collection handle");
  if (collectionHandle !== "all") return true;
  const url = collectionMembershipUrl(collectionHandle, 1);
  return Array.from(url.searchParams.keys()).some((name) => name.startsWith("filter.")) ||
    url.pathname.endsWith("/all") === false;
}

async function fetchCollectionProductIdPage(collectionHandle, page) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(collectionMembershipUrl(collectionHandle, page).toString(), {
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Collection membership request failed: ${response.status}`);
    const payload = await response.json();
    const validIds = Array.isArray(payload.product_ids) && payload.product_ids.every(
      (id) => /^\d+$/.test(String(id)) && Number(id) > 0,
    );
    if (!validIds || payload.page !== page || !Number.isInteger(payload.pages) ||
        payload.pages < 1 || payload.pages > 500 || !Number.isInteger(payload.items) || payload.items < 0) {
      throw new Error("Invalid collection membership response");
    }
    return { page: payload.page, pages: payload.pages, items: payload.items,
      productIds: payload.product_ids.map(String) };
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchCollectionProductIds(collectionHandle, expectedProductCount) {
  if (!needsCollectionMembership(collectionHandle)) return [];
  const first = await fetchCollectionProductIdPage(collectionHandle, 1);
  const pages = [first];
  for (let page = 2; page <= first.pages; page += 4) {
    const numbers = Array.from({ length: Math.min(4, first.pages - page + 1) }, (_, index) => page + index);
    pages.push(...await Promise.all(numbers.map((number) => fetchCollectionProductIdPage(collectionHandle, number))));
  }
  const ids = new Set();
  pages.forEach((page) => {
    if (page.pages !== first.pages || page.items !== first.items) {
      throw new Error("Collection membership changed during pagination");
    }
    page.productIds.forEach((id) => ids.add(id));
  });
  if (first.items !== expectedProductCount || ids.size !== first.items) {
    throw new Error("Incomplete collection membership; use native collection products");
  }
  return Array.from(ids);
}

class PartbotCollectionSearch extends HTMLElement {
  constructor() {
    super();

    this.totalProducts = parseInt(this.getAttribute("data-total-products"), 10);

    this.vehicleQueryParams = this.currentVehicleQueryParams();
    this.attachNativeCollectionControls();
    if (this.totalProducts === 0) return;

    this.collection = this.getAttribute("data-collection");
    this.defaultSortBy = this.getAttribute("data-sortby");
    this.currentSortValue = resolveSortParam(
      new URLSearchParams(window.location.search).get("sort_by") || this.defaultSortBy,
    );
    this.disjunctive = this.getAttribute("data-disjunctive") === "true" ? "OR" : "AND";
    this.aspectRatio = this.getAttribute("data-aspect-ratio") || "1.0";
    this.perPage = Math.min(parseInt(this.getAttribute("data-products-per-page"), 10) || 16, 20);

    this.skus = [];
    this.vehicle_ids = [];
    this.activeVehicleIds = [];
    this.selectedYear = null;
    this.brands = this.getAttribute("data-vendors")
      ? this.getAttribute("data-vendors").split("|")
      : [];
    this.product_types = this.getAttribute("data-product-types")
      ? this.getAttribute("data-product-types")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];

    this.facets = this.getAttribute("data-facets")
      ? this.getAttribute("data-facets")
          .split(",")
          .filter((str) => str.trim() !== "")
      : [];

    this.filters = this.getAttribute("data-filters");

    this.tags = this.getAttribute("data-tags")
      ? this.getAttribute("data-tags")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];

    this.showVehicleSearch = this.getAttribute("data-show-vehicle-search") === "true";

    this.partbot_vehicle = localStorage.getItem("partbot_selected_vehicle");
    this.partbot_plate_details = localStorage.getItem("partbot_plate_details");
    this.platesearch_ids = [];

    const hasPlateSearchVehicle =
      this.showVehicleSearch && this.partbot_vehicle && this.partbot_plate_details;

    if (hasPlateSearchVehicle) {
      this.partbot_vehicle = JSON.parse(this.partbot_vehicle);
      this.platesearch_ids = this.partbot_vehicle.vehicle_ids;

      const vehicleDescription =
        document.querySelector(".js-vehicle-description") ||
        document.querySelector(".partbot-vehicle-description");

      if (vehicleDescription) vehicleDescription.textContent = this.partbot_vehicle.description;

      const clearButton =
        document.querySelector(".js-button-clear-vehicle") ||
        document.querySelector(".partbot-clear-vehicle-button");
      if (clearButton) {
        clearButton.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();

          localStorage.removeItem("partbot_selected_vehicle");
          localStorage.removeItem("partbot_plate_details");
          location.reload();
        });
      }

      const vehicleNotice =
        document.querySelector(".js-selected-vehicle-notice") ||
        document.querySelector(".partbot-selected-vehicle-notice");

      if (vehicleNotice) {
        vehicleNotice.classList.remove("hidden");
        vehicleNotice.classList.add("visible", "flex");
      }

      document.querySelector("vehicle-search")?.classList.add("hidden");

      // add data-vehicle-selected="true" to the vehicle-search container section element
      document
        .querySelector("vehicle-search")
        ?.closest("section")
        ?.setAttribute("data-vehicle-selected", "true");
    }

    this.facetFiltersContainer =
      this.querySelector("#facet-filters") || this.querySelector("#partbot-facet-filters");

    const vehicleSearchRoot = document.querySelector("vehicle-search");
    this.apiKey =
      this.getAttribute("data-api-key") || vehicleSearchRoot?.getAttribute("data-api-key");
    this.store =
      this.getAttribute("data-store") ||
      vehicleSearchRoot?.getAttribute("data-store") ||
      window.location.hostname;
    this.apiHost = "https://api.cartbot.io";
    this.searchHost = "https://search.partbot.io";
    this.searchIndexName = null;
    this.debugPrefix = "[PartbotCollectionSearch][Bossco]";
    this.unsupportedFacetAttributes = new Set();
    this.baseFilters = "";
    this.collectionProductIdsFilter = "";
    this.searchFailed = false;
    this.configureWidget = null;
    this.configureWidgetParams = {};
    this.selectedFacetValues = new Map();
    this.facetLabelsByAttribute = new Map();
    this.facetContainersByAttribute = new Map();
    this.facetOptionState = new Map();
    this.facetSyncTimer = null;
    this.facetSyncToken = 0;
    this.hasReceivedSearchResults = false;
    this.vehicleQueryParams = this.currentVehicleQueryParams();

    this.hitsContainer = this.querySelector("#partbot-product-grid");
    this.cardRenderVersion = 0;
    this.cardHitsSignature = null;
    this.cardRenderedVersion = null;
    this.cardLoadVersion = null;
    this.cardLoadController = null;

    // hide the hitsContainer if there is a make parameter in the query string
    if (window.location.search.includes("make=")) {
      this.hitsContainer.style.display = "none";
    }

    // URL-derived vehicle search changes are emitted on window only.
    this.lastVehicleSearchEvent = null;
    this.onVehicleSearchChange = this.applyVehicleSearchSelection.bind(this);
    window.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    document.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    this.filterAlgoliaHits = this.processHits.bind(this);

    this.updateNativeVehicleFilterVisibility();

    this.initializeSearch().catch((error) => {
      console.error("[PartbotCollectionSearch] Failed to initialize search", error);
      this.showSearchFallback();
    });
  }

  showSearchFallback() {
    this.searchFailed = true;
    this.cardLoadController?.abort();
    const fallback = this.querySelector("#partbot-product-fallback");
    if (fallback) fallback.hidden = false;
    if (this.hitsContainer) this.hitsContainer.style.display = "none";
    const pagination = this.querySelector("#partbot-pagination");
    if (pagination) pagination.hidden = true;
    const status = this.querySelector("#partbot-search-status");
    if (status) status.textContent = "Search is temporarily unavailable. Showing collection products.";
    this.querySelectorAll(".partbot-filter-container, #partbot-clear-filters, #partbot-sortby, #sort-by")
      .forEach((control) => { control.hidden = true; });
    this.querySelectorAll('#CollectionFilterForm [data-partbot-hidden-duplicate="true"]')
      .forEach((group) => { group.hidden = false; delete group.dataset.partbotHiddenDuplicate; });
  }

  shouldPreserveQueryParam(name) {
    return !SHOPIFY_COLLECTION_PARAM_PATTERNS.some((pattern) => pattern.test(name));
  }

  currentVehicleQueryParams() {
    const preservedParams = new URLSearchParams();
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, name) => {
      if (this.shouldPreserveQueryParam(name)) {
        preservedParams.append(name, value);
      }
    });

    return preservedParams;
  }

  updateVehicleQueryParams(detail = {}) {
    const nextParams = this.currentVehicleQueryParams();
    const vehicle = detail.vehicle || {};

    VEHICLE_SEARCH_PARAM_NAMES.forEach((name) => {
      const value = vehicle[name]?.value || vehicle[name];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        nextParams.set(name, value);
      }
    });

    this.vehicleQueryParams = nextParams;
    this.updateNativeVehicleFilterVisibility();
  }

  hasVehicleSelection() {
    return (
      (Array.isArray(this.activeVehicleIds) && this.activeVehicleIds.length > 0) ||
      (Array.isArray(this.platesearch_ids) && this.platesearch_ids.length > 0) ||
      Array.from(this.vehicleQueryParams.keys()).some((name) =>
        VEHICLE_SEARCH_PARAM_NAMES.includes(name),
      )
    );
  }

  updateNativeVehicleFilterVisibility() {
    const hideVehicleFilters = this.hasVehicleSelection();
    const nativeFilterGroups = Array.from(
      this.querySelectorAll("#CollectionFilterForm .filter-group"),
    );

    nativeFilterGroups.forEach((group) => {
      const nativeTitle = normalizeFilterLabel(
        group.querySelector(".filter-group__heading__text")?.textContent,
      );

      if (!NATIVE_FILTER_LABELS_HIDDEN_WITH_VEHICLE.includes(nativeTitle)) return;

      if (hideVehicleFilters) {
        group.dataset.partbotHiddenVehicleSelected = "true";
        group.hidden = true;
      } else if (group.dataset.partbotHiddenVehicleSelected === "true") {
        delete group.dataset.partbotHiddenVehicleSelected;
        group.hidden = false;
      }
    });
  }

  collectionUrlFromNativeForm(form) {
    const params = new URLSearchParams();

    form.querySelectorAll("input, select").forEach((input) => {
      if (!input.name) return;
      if ((input.type === "checkbox" || input.type === "radio") && !input.checked) return;
      if (input.value === "") return;

      params.append(input.name, input.value);
    });

    this.vehicleQueryParams.forEach((value, name) => {
      if (!params.has(name)) params.append(name, value);
    });

    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ""}`;
  }

  collectionUrlFromNativeLink(link) {
    const url = new URL(link.href, window.location.origin);
    this.vehicleQueryParams.forEach((value, name) => {
      if (!url.searchParams.has(name)) url.searchParams.append(name, value);
    });
    return url.toString();
  }

  attachNativeCollectionControls() {
    this.addEventListener("click", (event) => {
      const link = event.target.closest("a.filter-group__applied-item, a.filter-group__clear-link, #partbot-product-fallback .pagination a");
      if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = this.collectionUrlFromNativeLink(link);
    }, true);

    const form = this.querySelector("#CollectionFilterForm");
    if (!form) return;

    const navigateWithPartbotState = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      window.location.href = this.collectionUrlFromNativeForm(form);
    };

    form.addEventListener("change", navigateWithPartbotState, true);
    form.addEventListener("submit", navigateWithPartbotState, true);
  }

  extractUnsupportedFacetAttributes(error) {
    const message = error?.message || String(error || "");
    const match = message.match(/Attributes? `([^`]+)` (?:are|is) not filterable/);
    if (!match) return [];

    return match[1]
      .split(",")
      .map((attribute) => attribute.trim())
      .filter(Boolean);
  }

  sanitizeMeiliQueries(queries) {
    if (!Array.isArray(queries) || this.unsupportedFacetAttributes.size === 0) {
      return queries;
    }

    return queries.map((query) => {
      if (!query || !Array.isArray(query.facets)) {
        return query;
      }

      const facets = query.facets.filter(
        (attribute) => !this.unsupportedFacetAttributes.has(attribute),
      );

      return facets.length === query.facets.length ? query : { ...query, facets };
    });
  }

  sanitizeInstantSearchRequests(requests) {
    if (!Array.isArray(requests) || this.unsupportedFacetAttributes.size === 0) {
      return requests;
    }

    return requests.map((request) => {
      if (!request?.params) return request;

      const sanitizedParams = { ...request.params };

      if (Array.isArray(sanitizedParams.facets)) {
        sanitizedParams.facets = sanitizedParams.facets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      if (Array.isArray(sanitizedParams.disjunctiveFacets)) {
        sanitizedParams.disjunctiveFacets = sanitizedParams.disjunctiveFacets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      return { ...request, params: sanitizedParams };
    });
  }

  removeUnsupportedFacets(attributes) {
    if (!attributes.length) return [];

    const removedFacets = [];

    attributes.forEach((attribute) => {
      if (this.unsupportedFacetAttributes.has(attribute)) return;
      this.unsupportedFacetAttributes.add(attribute);

      const matchingFacets = this.facets.filter(
        (facet) => normalizeFacetAttribute(facet) === attribute,
      );
      if (matchingFacets.length === 0) return;

      matchingFacets.forEach((facet) => {
        removedFacets.push(facet);
        this.removeFacetContainer(facet);
      });

      this.facets = this.facets.filter((facet) => normalizeFacetAttribute(facet) !== attribute);
    });

    return removedFacets;
  }

  removeFacetContainer(facet) {
    const facetTitle = facet.replace(/_/g, " ").replace("vendor", "brand");
    const containers = Array.from(this.querySelectorAll(".partbot-filter-container"));
    const container = containers.find(
      (node) =>
        node.querySelector(".partbot-filter-heading-text")?.textContent?.trim() === facetTitle,
    );

    if (container) {
      container.remove();
    }

    this.showMatchingNativeFilterGroup(facetTitle);
  }

  hideMatchingNativeFilterGroup(facetTitle) {
    const normalizedFacetTitle = normalizeFilterLabel(facetTitle);
    if (!normalizedFacetTitle) return;

    const nativeFilterGroups = Array.from(
      this.querySelectorAll("#CollectionFilterForm .filter-group"),
    );

    nativeFilterGroups.forEach((group) => {
      if (group.dataset.partbotHiddenDuplicate === "true") return;

      const nativeTitle = normalizeFilterLabel(
        group.querySelector(".filter-group__heading__text")?.textContent,
      );

      if (nativeTitle !== normalizedFacetTitle) return;

      group.dataset.partbotHiddenDuplicate = "true";
      group.hidden = true;
    });
  }

  showMatchingNativeFilterGroup(facetTitle) {
    const normalizedFacetTitle = normalizeFilterLabel(facetTitle);
    if (!normalizedFacetTitle) return;

    const hiddenGroups = Array.from(
      this.querySelectorAll('#CollectionFilterForm .filter-group[data-partbot-hidden-duplicate="true"]'),
    );

    hiddenGroups.forEach((group) => {
      const nativeTitle = normalizeFilterLabel(
        group.querySelector(".filter-group__heading__text")?.textContent,
      );

      if (nativeTitle !== normalizedFacetTitle) return;

      delete group.dataset.partbotHiddenDuplicate;
      group.hidden = false;
    });
  }

  unsupportedFacetStorageKey() {
    return `partbot_unsupported_facets:${this.searchIndexName || this.store || "default"}`;
  }

  loadUnsupportedFacetAttributes() {
    try {
      const stored = localStorage.getItem(this.unsupportedFacetStorageKey());
      if (!stored) return;

      JSON.parse(stored).forEach((attribute) => {
        if (attribute) this.unsupportedFacetAttributes.add(attribute);
      });
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to load unsupported facets`, error);
    }
  }

  persistUnsupportedFacetAttributes() {
    try {
      localStorage.setItem(
        this.unsupportedFacetStorageKey(),
        JSON.stringify(Array.from(this.unsupportedFacetAttributes)),
      );
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to persist unsupported facets`, error);
    }
  }

  selectedFacetFilter(excludeAttribute = null) {
    return Array.from(this.selectedFacetValues.entries())
      .filter(([attribute, values]) => attribute !== excludeAttribute && values.size > 0)
      .map(([attribute, values]) => {
        const valueFilters = Array.from(values).map((value) => equalsFilter(attribute, value));
        return `(${valueFilters.join(" OR ")})`;
      })
      .join(" AND ");
  }

  helperRefinementFilter(excludeAttribute = null) {
    const state = this.search?.helper?.state;
    if (!state) return "";

    const groups = [];
    const addGroup = (attribute, values) => {
      if (
        attribute === excludeAttribute ||
        this.selectedFacetValues.has(attribute) ||
        !Array.isArray(values) ||
        values.length === 0
      ) {
        return;
      }

      groups.push(
        `(${values
          .map((value) => equalsFilter(attribute, value, { numeric: attribute === "vehicle_ids" }))
          .join(" OR ")})`,
      );
    };

    Object.entries(state.facetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    Object.entries(state.disjunctiveFacetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    return groups.join(" AND ");
  }

  selectedVehicleYearFilter() {
    if (
      !this.selectedYear ||
      !Array.isArray(this.activeVehicleIds) ||
      this.activeVehicleIds.length === 0
    ) {
      return "";
    }

    const maxIndexedYear = Math.max(this.selectedYear, new Date().getFullYear() + 1);
    const filters = [];

    for (let year = this.selectedYear; year <= maxIndexedYear; year += 1) {
      this.activeVehicleIds.forEach((vehicleId) => {
        filters.push(equalsFilter(`vehicle_years.year_to.${year}`, vehicleId));
      });
    }

    return filters.length > 0 ? `(${filters.join(" OR ")})` : "";
  }

  nativePriceRangeFilter() {
    const params = new URLSearchParams(window.location.search);
    const priceFrom = priceFilterValue(params.get("filter.v.price.gte"));
    const priceTo = priceFilterValue(params.get("filter.v.price.lte"));
    const filters = [];

    if (priceFrom !== null) filters.push(`price_from >= ${priceFrom}`);
    if (priceTo !== null) filters.push(`price_from <= ${priceTo}`);

    return filters.join(" AND ");
  }

  buildSearchFilters(excludeAttribute = null, { includeHelperRefinements = false } = {}) {
    return [
      this.baseFilters,
      this.collectionProductIdsFilter ? `(${this.collectionProductIdsFilter})` : "",
      this.nativePriceRangeFilter(),
      this.selectedVehicleYearFilter(),
      this.selectedFacetFilter(excludeAttribute),
      includeHelperRefinements ? this.helperRefinementFilter(excludeAttribute) : "",
    ]
      .map((filter) => String(filter || "").trim())
      .filter(Boolean)
      .join(" AND ");
  }

  currentConfigureParams() {
    return {
      ...this.configureWidgetParams,
      filters: this.buildSearchFilters(),
    };
  }

  requestsWithCurrentFilters(requests) {
    if (!Array.isArray(requests)) return requests;

    const filters = this.buildSearchFilters();
    return requests.map((request) => {
      if (!request?.params) return request;

      return {
        ...request,
        params: {
          ...request.params,
          filters,
        },
      };
    });
  }

  updateConfigureWidget() {
    if (!this.search || !this.configureWidget) return;

    this.search.removeWidgets([this.configureWidget]);
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);
  }

  renderSortControls() {
    const sortByContainer = this.querySelector("#sort-by") || this.querySelector("#partbot-sortby");
    if (!sortByContainer) return;

    const options = [
      { label: "Relevance", value: "" },
      { label: "Best Selling", value: "quantity_sold:desc" },
      { label: "Price (High to Low)", value: "price_from:desc" },
      { label: "Price (Low to High)", value: "price_from:asc" },
      { label: "Alphabetical (A-Z)", value: "title:asc" },
      { label: "Alphabetical (Z-A)", value: "title:desc" },
    ];

    sortByContainer.innerHTML = `
      <div class="w-full md:w-auto">
        <select class="rounded-md border text-xs text-gray-500 border-gray-300 focus:ring-red-500 focus:border-red-500 w-full" aria-label="Sort products">
          ${options
            .map(
              (option) => `
                <option value="${option.value}" ${option.value === this.currentSortValue ? "selected" : ""}>
                  ${option.label}
                </option>
              `,
            )
            .join("")}
        </select>
      </div>
    `;

    const select = sortByContainer.querySelector("select");
    select?.addEventListener("change", (event) => {
      this.currentSortValue = event.target.value;
      const nextIndexName = this.currentSortValue
        ? `${this.searchIndexName}:${this.currentSortValue}`
        : this.searchIndexName;

      this.showSearchLoadingIndicator();
      this.search?.helper?.setIndex?.(nextIndexName);
      this.search?.helper?.setPage?.(0);
      this.search?.helper?.search?.();
    });
  }

  hasSelectedFacetValues() {
    return Array.from(this.selectedFacetValues.values()).some((values) => values.size > 0);
  }

  setFacetSelection(attribute, value, selected) {
    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();

    if (selected) {
      selectedValues.add(value);
      this.selectedFacetValues.set(attribute, selectedValues);
    } else {
      selectedValues.delete(value);
      if (selectedValues.size > 0) {
        this.selectedFacetValues.set(attribute, selectedValues);
      } else {
        this.selectedFacetValues.delete(attribute);
      }
    }

    this.renderFacetOptions(attribute);
    this.renderClearFiltersButton();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
  }

  clearFacetSelections() {
    if (!this.hasSelectedFacetValues()) return;

    this.selectedFacetValues.clear();
    this.facetOptionState.forEach((_, attribute) => this.renderFacetOptions(attribute));
    this.renderClearFiltersButton();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
  }

  mergeFacetOptions(attribute, facetData, { replace = false } = {}) {
    const options = replace
      ? new Map()
      : new Map(Array.from(this.facetOptionState.get(attribute) || new Map()));

    Object.entries(facetData || {}).forEach(([value, count]) => {
      options.set(value, Number(count) || 0);
    });

    const selectedValues = this.selectedFacetValues.get(attribute);
    if (selectedValues) {
      selectedValues.forEach((value) => {
        if (!options.has(value)) options.set(value, 0);
      });
    }

    this.facetOptionState.set(attribute, options);
  }

  facetOptionsFromResults(results, attribute) {
    if (!results?.getFacetByName) return {};

    try {
      return results.getFacetByName(attribute)?.data || {};
    } catch (error) {
      return {};
    }
  }

  renderFacetOptions(attribute) {
    const containerSelector = this.facetContainersByAttribute.get(attribute);
    const container = containerSelector ? this.querySelector(containerSelector) : null;
    if (!container) return;

    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();
    const options = this.facetOptionState.get(attribute) || new Map();
    const items = Array.from(options.entries())
      .map(([value, count]) => ({
        value,
        label: value === "true" ? "Yes" : value === "false" ? "No" : value,
        count,
        isRefined: selectedValues.has(value),
      }))
      .sort(
        (a, b) =>
          Number(b.isRefined) - Number(a.isRefined) ||
          a.label.localeCompare(b.label, undefined, { numeric: true }) ||
          b.count - a.count,
      );

    container.innerHTML = `
      <div class="toggle-target-container partbot-filter-values partbot-filter-list">
        ${items
          .map(
            ({ value, label, count, isRefined }) => `
              <label
                class="filter-group__item partbot-filter${isRefined ? " filter-group__item--active" : ""}"
                data-value="${encodeURIComponent(value)}"
              >
                <input
                  type="checkbox"
                  ${isRefined ? "checked" : ""}
                  class="filter-group__checkbox"
                />
                <span class="filter-group__item__text filter-value">${escapeHtml(label)}</span>
                <span class="filter-group__item__count filter-count">${count}</span>
              </label>
            `,
          )
          .join("")}
      </div>
    `;
  }

  renderClearFiltersButton() {
    const container = document.querySelector("#partbot-clear-filters");
    if (!container) return;

    const disabled = !this.hasSelectedFacetValues();
    container.innerHTML = `
      <button type="button" class="btn btn-sm btn-secondary disabled:hidden" ${
        disabled ? "disabled" : ""
      }>
        <span>Clear All</span>
      </button>
    `;

    const button = container.querySelector("button");
    if (button) {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.clearFacetSelections();
      });
    }
  }

  showSearchLoadingIndicator() {
    if (this.searchFailed) return;
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    status.innerHTML = `
      <span class="inline-flex items-center gap-2">
        <span>Loading</span>
        <span
          aria-hidden="true"
          class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
        ></span>
      </span>
    `;
  }

  renderSearchStatus(count = null) {
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    const productCount = Number(count);
    if (!this.hasReceivedSearchResults || !Number.isFinite(productCount)) {
      this.showSearchLoadingIndicator();
      return;
    }

    status.textContent = `Showing ${productCount} ${productCount === 1 ? "product" : "products"}`;
  }

  scheduleFacetOptionSync() {
    if (!this.searchKey || !this.searchHost || !this.searchIndexName) return;

    clearTimeout(this.facetSyncTimer);
    this.facetSyncTimer = setTimeout(() => {
      this.syncFacetOptionCounts().catch((error) => {
        console.warn(`${this.debugPrefix} failed to sync facet option counts`, error);
      });
    }, 100);
  }

  async syncFacetOptionCounts() {
    const attributes = Array.from(this.facetContainersByAttribute.keys());
    if (attributes.length === 0) return;

    const token = ++this.facetSyncToken;
    const query = this.search?.helper?.state?.query || "";
    const queries = attributes.map((attribute) => ({
      indexUid: this.searchIndexName,
      q: query,
      limit: 0,
      facets: [attribute],
      filter: this.buildSearchFilters(attribute, { includeHelperRefinements: true }),
    }));

    const response = await fetch(`${this.searchHost}/multi-search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.searchKey}`,
      },
      body: JSON.stringify({ queries }),
    });

    if (!response.ok) {
      throw new Error(`Facet option sync failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (token !== this.facetSyncToken) return;

    (data.results || []).forEach((result, index) => {
      const attribute = attributes[index];
      this.mergeFacetOptions(attribute, result.facetDistribution?.[attribute] || {}, {
        replace: true,
      });
      this.renderFacetOptions(attribute);
    });
  }

  async initializeSearch() {
    if (!this.apiKey) {
      throw new Error("Missing data-api-key on vehicle-search or partbot-collection-search");
    }

    if (!window.instantsearch) {
      throw new Error("instantsearch.js must be loaded before PartbotCollectionSearch");
    }

    await loadScriptOnce(
      "https://cdn.jsdelivr.net/npm/@meilisearch/instant-meilisearch/dist/instant-meilisearch.umd.min.js",
      "instantMeiliSearch",
    );

    const auth = await fetchSearchConfig(this.apiHost, this.apiKey, this.store);
    this.searchHost = normalizeSearchHost(auth.host, this.searchHost);
    this.searchKey = auth.store_search_key || auth.search_key;
    this.searchIndexName = auth.store_index_name || this.getAttribute("data-algolia-index");


    if (!this.searchKey || !this.searchIndexName) {
      throw new Error("Vehicle auth did not return store_search_key/search_key and store_index_name");
    }

    this.collectionProductIds = await fetchCollectionProductIds(this.collection, this.totalProducts);

    this.loadUnsupportedFacetAttributes();
    const persistedRemovedFacets = this.removeUnsupportedFacets(
      Array.from(this.unsupportedFacetAttributes),
    );
    if (persistedRemovedFacets.length > 0) {
    }

    const { searchClient } = window.instantMeiliSearch(this.searchHost, this.searchKey);
    const originalSearch = searchClient.search.bind(searchClient);
    searchClient.search = async (requests) => {
      const sanitizedRequests = this.sanitizeInstantSearchRequests(requests);


      try {
        const response = await originalSearch(sanitizedRequests);
        return response;
      } catch (error) {
        const unsupportedFacetAttributes = this.extractUnsupportedFacetAttributes(error);
        const removedFacets = this.removeUnsupportedFacets(unsupportedFacetAttributes);

        if (removedFacets.length > 0) {
          this.persistUnsupportedFacetAttributes();
          const retriedRequests = this.sanitizeInstantSearchRequests(requests);

          console.warn(`${this.debugPrefix} dropped unsupported facets and retrying`, {
            unsupportedFacetAttributes,
            removedFacets,
          });

          const response = await originalSearch(retriedRequests);
          return response;
        }

        if (isUnsupportedSortError(error)) {
          const retriedRequests = sortedRequestsOnPrimaryIndex(sanitizedRequests, this.searchIndexName, {
            dropSort: true,
          });

          console.warn(`${this.debugPrefix} dropped unsupported sort and retrying`, {
            indexName: this.searchIndexName,
            error,
          });

          const response = await originalSearch(retriedRequests);
          return response;
        }

        console.error(`${this.debugPrefix} search error`, {
          indexName: this.searchIndexName,
          requests: sanitizedRequests,
          error,
        });
        throw error;
      }
    };

    this.search = instantsearch({
      indexName: resolveInitialSortIndex(
        this.searchIndexName,
        new URLSearchParams(window.location.search).get("sort_by") || this.defaultSortBy,
      ),
      searchClient,
      routing: false,
      searchFunction: (helper) => {
        if (this.vehicle_ids.length > 0) {
          this.vehicle_ids.map((id) => {
            helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
          });
          this.vehicle_ids = [];
        }
        helper.search();
      },
      onStateChange: ({ uiState, setUiState }) => {
        this.showHitsContainerForVehicleUrl();

        setUiState(uiState);
      },
    });

    let vehicleIdFilter = "";
    if (this.showVehicleSearch && this.platesearch_ids.length > 0) {
      let vehicle_id_filters = this.platesearch_ids
        .map((vehicle_id) => equalsFilter("vehicle_ids", vehicle_id, { numeric: true }))
        .join(" OR ");
      vehicleIdFilter = `${vehicle_id_filters}`;
    }

    let filters = "";
    if (this.brands.length > 0) {
      let brand_filters = this.brands
        .map((brand) => equalsFilter("brand", brand))
        .join(` ${this.disjunctive} `);
      filters = `(${brand_filters})`;
    }

    if (this.product_types.length > 0) {
      let product_type_filters = this.product_types
        .map((product_type) => {
          // Decode Unicode escape sequences and then escape single quotes
          const decodedType = product_type
            .replace(/\\u[\dA-F]{4}/gi, (match) =>
              String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16)),
            )
          return equalsFilter("product_type", decodedType);
        })
        .join(` ${this.disjunctive} `);
      filters = filters
        ? `${filters} ${this.disjunctive} (${product_type_filters})`
        : `(${product_type_filters})`;
    }

    const configuredFilters = normalizeLegacySearchFilters(this.filters);
    if (configuredFilters) {
      filters = filters ? `(${filters}) AND (${configuredFilters})` : `(${configuredFilters})`;
    }

    if (vehicleIdFilter && vehicleIdFilter.length > 0) {
      filters = filters ? `(${filters}) AND (${vehicleIdFilter})` : `(${vehicleIdFilter})`;
    }

    if (this.tags.length > 0) {
      let tag_filters = this.tags
        .map((tag) => equalsFilter("tags", tag))
        .join(` ${this.disjunctive} `);
      filters = filters
        ? `(${filters}) ${this.disjunctive} (${tag_filters})`
        : `(${tag_filters})`;
    }

    // add published:true to filters
    filters = filters ? `(${filters}) AND published = true` : `published = true`;
    filters = `(${filters}) AND saleable = true`;

    // filter out listing_status:invalid
    // filters = `(${filters}) AND listing_status != ${filterValue("invalid")}`;

    if (needsCollectionMembership(this.collection)) {
      if (this.collectionProductIds.length === 0) throw new Error("Empty collection membership");
      this.collectionProductIdsFilter = this.collectionProductIds
        .map((id) => equalsFilter("objectID", id, { numeric: true }))
        .join(" OR ");
    }

    const configuredFacetAttributes = uniq(
      this.facets.map((facet) => normalizeFacetAttribute(facet)),
    );


    this.baseFilters = filters;
    this.configureWidgetParams = {
      hitsPerPage: this.perPage,
      distinct: true,
      maxValuesPerFacet: 100,
      facetingAfterDistinct: true,
      facets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
      disjunctiveFacets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_ids",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
    };
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);

    // Add Facet Filters
    if (this.facets && this.facets.length) {
      this.facets.forEach((facet) => {
        if (!this.facetFiltersContainer) return;

        let node = document.createElement("div");
        node.classList.add("partbot-filter-container", "filter-group", "filter-group--layout-two-column");
        let facet_id = facet.replace(/[\s_()]+/g, "-").toLowerCase();
        const facetTitle = facet.replace(/_/g, " ").replace("vendor", "brand");
        const headingId = `partbot-filter-heading-${facet_id}`;
        node.innerHTML = `
          <a href="#" class="filter-group__heading partbot-filter-title" data-toggle-target="#partbot-filter-${facet_id}">
            <div class="filter-group__heading__text">${escapeHtml(facetTitle)}</div>
            <span class="filter-group__heading__indicator">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            </span>
          </a>
          <div id="${headingId}" class="partbot-filter-heading-text" hidden>${escapeHtml(facetTitle)}</div>
          <div id="partbot-filter-${facet_id}" class="filter-group__items toggle-target"></div>
        `;

        let attr_title = normalizeFacetAttribute(facet);

        this.facetLabelsByAttribute.set(attr_title, facet);
        this.facetContainersByAttribute.set(attr_title, `#partbot-filter-${facet_id}`);
        this.facetOptionState.set(attr_title, new Map());
        this.facetFiltersContainer.appendChild(node);
        this.hideMatchingNativeFilterGroup(facetTitle);

        node.querySelector(".partbot-filter-title")?.addEventListener("click", (event) => {
          event.preventDefault();
          node.querySelector(".toggle-target")?.classList.toggle("toggle-target--hidden");
          event.currentTarget.classList.toggle("toggle-target-toggler--is-hidden");
        });

        const container = this.querySelector(`#partbot-filter-${facet_id}`);
        if (container) {
          container.addEventListener("change", (event) => {
            if (!event.target.matches('input[type="checkbox"]')) return;

            const itemElement = event.target.closest(".partbot-filter");
            const value = decodeURIComponent(itemElement.getAttribute("data-value") || "");
            if (!value) return;

            this.setFacetSelection(attr_title, value, event.target.checked);
          });
        }

        this.search.addWidgets([
          {
            render: ({ results }) => {
              this.mergeFacetOptions(attr_title, this.facetOptionsFromResults(results, attr_title));
              this.renderFacetOptions(attr_title);
            },
          },
        ]);
      });
    }

    // add clear filters button
    this.renderClearFiltersButton();

    this.renderSortControls();

    this.search.addWidget(
      instantsearch.widgets.pagination({
        container: "#partbot-pagination",
        autoHideContainer: true,
        padding: 1,
        scrollTo: "#partbot-product-grid",
        templates: {
          first: "<<",
          previous: "<",
          next: ">",
          last: ">>",
        },
        cssClasses: {
          list: "partbot-pagination-list",
          selectedItem: "active",
          link: "partbot-pagination-button",
          disabledItem: "disabled hidden",
          firstPageItem: "partbot-pagination-control",
          lastPageItem: "partbot-pagination-control",
          nextPageItem: "partbot-pagination-control",
          previousPageItem: "partbot-pagination-control",
        },
      }),
    );

    const PREFERRED_VENDOR = "Bossco 4x4 Accessories";

    // Add Hits Widget
    const filterM = this.filterAlgoliaHits;
    let _this = this;
    const customHits = instantsearch.connectors.connectHits(
      ({ hits, widgetParams }, isFirstRendering) => {
        if (isFirstRendering) return;

        const transformedHits = widgetParams.transformItems
          ? widgetParams.transformItems(hits)
          : hits;

        const hitsContainer = document.querySelector(widgetParams.container);
        if (!hitsContainer) return;

        _this.renderProductHits(hitsContainer, transformedHits);
      },
    );

    this.search.addWidget(
      customHits({
        container: "#partbot-product-grid",
        transformItems(items) {
          _this.skus = [];
          const filteredItems = items.filter(filterM, this).filter((i) => i);
          const isBestSelling = _this.currentSortValue === "quantity_sold:desc";

          if (isBestSelling) {
            return filteredItems.sort((a, b) => {
              if (a.brand === PREFERRED_VENDOR && b.brand !== PREFERRED_VENDOR) return -1;
              if (a.brand !== PREFERRED_VENDOR && b.brand === PREFERRED_VENDOR) return 1;
              return 0;
            });
          }

          return filteredItems;
        },
      }),
    );

    this.pendingCardLoad = null; // Add this to track pending loads

    this.search.on("error", (error) => {
      console.error(`${this.debugPrefix} search failed`, error);
      this.showSearchFallback();
    });

    this.search.on("render", () => {
      if (this.searchFailed) return;
      this.scheduleFacetOptionSync();
      const results = this.search.helper?.lastResults;
      if (results) {
        this.hasReceivedSearchResults = true;
        this.lastResultsCount =
          typeof results.nbHits === "number" ? results.nbHits : results.hits?.length;
      }

      this.scheduleProductCardLoad(results);

    });

    this.search.start();
    this.showSearchLoadingIndicator();
  }

  renderProductHits(hitsContainer, hits) {
    const signature = JSON.stringify(hits.map((hit) => [String(hit.objectID), hit.handle]));
    if (this.cardHitsSignature === signature) return;
    this.cardHitsSignature = signature;
    this.cardRenderVersion += 1;
    this.cardLoadController?.abort();

    hitsContainer.innerHTML =
      hits.length > 0
        ? hits
            .map(
              (hit) =>
                `<div class="product-card-placeholder" data-handle="${escapeHtml(hit.handle)}"></div>`,
            )
            .join("")
        : `<div class="partbot-results-empty">
        <div class="max-w-7xl p-12 lg:py-20">
          <h2 class="text-4xl font-bold tracking-tight text-gray-900 mb-4">
            No results found.
          </h2>
        </div>
      </div>`;
  }

  scheduleProductCardLoad(results) {
    const version = this.cardRenderVersion;
    if (this.cardRenderedVersion === version) {
      this.renderSearchStatus(this.lastResultsCount);
      return;
    }
    if (this.cardLoadVersion === version) return;
    if (this.pendingCardLoad) clearTimeout(this.pendingCardLoad);
    this.pendingCardLoad = setTimeout(() => {
      this.pendingCardLoad = null;
      if (this.searchFailed || this.cardRenderVersion !== version) return;
      this.cardLoadVersion = version;
      this.cardLoadController = new AbortController();
      const isCurrent = () => !this.searchFailed && this.cardRenderVersion === version;
      window.scrollTo(0, 0);
      this.showSearchLoadingIndicator();
      loadProductCards(this.collection, this.aspectRatio, {
        container: this.hitsContainer,
        signal: this.cardLoadController.signal,
        isCurrent,
      }).then(() => {
        if (!isCurrent() || !results) return;
        this.cardRenderedVersion = version;
        const fallback = this.querySelector("#partbot-product-fallback");
        if (fallback) fallback.hidden = true;
        this.hitsContainer.style.display = "";
        this.renderSearchStatus(this.lastResultsCount);
      }).catch((error) => {
        if (!isCurrent()) return;
        console.error(`${this.debugPrefix} product cards failed`, error);
        this.showSearchFallback();
      });
    }, 100);
  }

  cacheVehicleSearchSelection(detail = {}) {
    this.updateVehicleQueryParams(detail);

    this.vehicle_ids = Array.isArray(detail.vehicle_ids) ? detail.vehicle_ids : [];
    this.activeVehicleIds = this.vehicle_ids;

    if (detail.vehicle?.year?.value) {
      this.selectedYear = parseInt(detail.vehicle.year.value, 10);
    } else {
      this.selectedYear = null;
    }

    this.updateNativeVehicleFilterVisibility();
    this.showHitsContainerForVehicleUrl();
  }

  showHitsContainerForVehicleUrl() {
    if (this.searchFailed) return;
    if (
      this.hitsContainer &&
      this.activeVehicleIds.length > 0 &&
      window.location.search.includes("make=")
    ) {
      this.hitsContainer.style.removeProperty("display");
    }
  }

  processHits(item) {
    if (this.selectedYear) {
      // collect all year_froms <= selectedYear and year_tos >= selectedYear
      var yf_vehicle_ids = [];
      var yt_vehicle_ids = [];
      var yf_permitted, yt_permitted;
      if (
        item?.vehicle_years?.year_from !== undefined &&
        item?.vehicle_years?.year_to !== undefined
      ) {
        // collect year_froms
        for (var year in item.vehicle_years.year_from) {
          if (year <= this.selectedYear) {
            yf_vehicle_ids.push(item.vehicle_years.year_from[year]);
          }
        }

        // collect year_tos
        for (var year in item.vehicle_years.year_to) {
          if (year >= this.selectedYear) {
            yt_vehicle_ids.push(item.vehicle_years.year_to[year]);
          }
        }

        yf_vehicle_ids = yf_vehicle_ids.flat();
        yt_vehicle_ids = yt_vehicle_ids.flat();

        yf_permitted = this.activeVehicleIds.filter((n) => yf_vehicle_ids.includes(n));

        yt_permitted = this.activeVehicleIds.filter((n) => yt_vehicle_ids.includes(n));

        // remove from results if not permitted
        if (yf_permitted.length === 0 || yt_permitted.length === 0) {
          return false;
        }
      }
    }

    // truncate item.title to 140 characters
    if (item.title.length > 140) {
      item.title = item.title.substring(0, 140) + "...";
    }

    // is multi_variant?
    item.multi_variant = item.variant_skus && item.variant_skus.length > 1;

    // check if the item has a compare_at_price_from value and if it is less than or equal to the price_from value set it to null
    if (item.compare_at_price_from && item.compare_at_price_from <= item.price_from) {
      item.compare_at_price_from = null;
    }

    return item;
  }

  applyVehicleSearchSelection(e) {
    if (this.searchFailed) return false;
    if (e && this.lastVehicleSearchEvent === e) return false;
    this.lastVehicleSearchEvent = e;

    const detail = e.detail || {};

    // if (this.platesearch_ids && this.platesearch_ids.length) {
    //   return false;
    // }

    this.cacheVehicleSearchSelection(detail);

    if (!this.search?.helper) {
      return false;
    }

    this.updateConfigureWidget();

    this.vehicle_ids = [];
    this.search.helper.clearRefinements("vehicle_ids");
    this.search.helper.clearRefinements("vehicle_makes");

    if (detail.vehicle?.make) {
      this.search.helper.addFacetRefinement("vehicle_makes", detail.vehicle.make.value);
    }

    if (this.activeVehicleIds.length > 0) {
      this.activeVehicleIds.map((id) => {
        this.search.helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
      });
    }

    this.search.helper.search();
  }
}

function fetchProductCards(handles, collection, aspectRatio = "1.0", signal) {
  if (handles.length === 0) {
    return Promise.resolve([]);
  }

  const cacheBuster = new Date().getTime();
  const seedHandles = handles.filter(Boolean);
  const parser = new DOMParser();

  const fetchBatch = (seedHandle) => {
    const url = `products/${seedHandle}?view=partbot-product-cards&c=${collection}&ar=${aspectRatio}${handles
      .filter((handle) => handle && handle !== seedHandle)
      .map((handle) => `&h=${encodeURIComponent(handle)}`)
      .join("")}&cache=false&type=${cacheBuster}`;

    const routesRoot = window.Shopify?.routes?.root || "/";
    const root = routesRoot.endsWith("/") ? routesRoot : `${routesRoot}/`;

    return fetch(`${root}${url}`, {
      signal,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    });
  };

  const parseCards = (html) => {
    const doc = parser.parseFromString(html, "text/html");
    const productCards = doc.querySelectorAll(".product-block");
    const cardMap = new Map();

    productCards.forEach((card) => {
      const handle = card.dataset.handle;
      cardMap.set(handle, card.outerHTML);
    });

    return cardMap;
  };

  const trySeedHandle = (index = 0, lastError = null) => {
    if (index >= seedHandles.length) {
      if (lastError) throw lastError;
      return [];
    }

    return fetchBatch(seedHandles[index])
      .then(parseCards)
      .then((cardMap) => {
        if (cardMap.size > 0) return cardMap;
        return trySeedHandle(
          index + 1,
          new Error("No product cards returned from partbot-product-cards template"),
        );
      })
      .catch((error) => {
        if (signal?.aborted) throw error;
        return trySeedHandle(index + 1, error);
      });
  };

  return trySeedHandle();
}

function loadProductCards(collection, aspectRatio, { container = document.querySelector("#partbot-product-grid"), signal, isCurrent = () => true } = {}) {
  const existingIndicators = document.querySelectorAll("loading-indicator");
  existingIndicators.forEach((indicator) => indicator.remove());

  const loadingIndicator = document.createElement("loading-indicator");

  if (!container) return Promise.resolve();

  container.parentElement.appendChild(loadingIndicator);

  const placeholders = Array.from(container.querySelectorAll(".product-card-placeholder"));

  const handles = placeholders.map((placeholder) => placeholder.dataset.handle).filter(Boolean);

  if (handles.length === 0) {
    loadingIndicator.remove();
    return Promise.resolve();
  }

  return fetchProductCards(handles, collection, aspectRatio, signal)
    .then((cardMap) => {
      if (!isCurrent()) return;
      placeholders.forEach((placeholder) => {
        if (!placeholder.parentNode) return;

        const cardHtml = cardMap.get(placeholder.dataset.handle);
        if (cardHtml) {
          placeholder.outerHTML = cardHtml;
        } else {
          placeholder.remove();
        }
      });
    })
    .finally(() => {
      loadingIndicator.remove();
    });
}

// move the loading indicator to a component

customElements.define("partbot-collection-search", PartbotCollectionSearch);
